import { useContext, useEffect, HTMLAttributes } from "react";
import { useIntl } from "react-intl";
import { SummaryCard, IconLibrary, FieldList, TooltipWrapper } from "@10duke/dukeui";
import { OrganizationGroup } from "../../../model/OrganizationGroup";
import { Link } from "react-router-dom";
import { ModalKeys } from "../../pages/groups";
import { ButtonGroup } from "react-bootstrap";
import { sortToLatestByCreatedOrUpdated } from "../../../util/objectUtil";
import GroupTypeBadge from "../../badges/group-type-badge";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import {OrgadminSectionConfiguration} from "../../../ui-configuration/orgadmin-configuration";
import {hasAction} from "../../../ui-configuration/configuration-tools";

interface GroupsCardDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {}
export interface GroupsCardProps extends GroupsCardDOMProps {
  isReady: boolean;
  groups?: OrganizationGroup[] | null;
  onLoadGroups?: () => {};

  moreAction: () => void;
}

function GroupsCard(props: GroupsCardProps) {
  const { groups, onLoadGroups, moreAction, isReady } = props;
  const intl = useIntl();
  const { conf } = useContext(UIConfiguration);
  const groupsConf =
    conf.functionality && conf.functionality.groups
      ? conf.functionality.groups
      : ({} as OrgadminSectionConfiguration);
  const headerButton = {
    label: intl.formatMessage({
      defaultMessage: "View",
      description: "View button label",
    }),
    action: moreAction,
  };
  if (!onLoadGroups && groups === undefined) {
    throw new Error(
      "GroupsCard: Required props missing. Either groups or onLoadGroups must be defined"
    );
  }
  const isGroupsUndefined = groups === undefined;
  useEffect(() => {
    if (isGroupsUndefined && onLoadGroups) {
      onLoadGroups();
    }
  }, [isGroupsUndefined, onLoadGroups]);
  return (
    <SummaryCard
      onReloadData={() => {
        if (onLoadGroups) {
          onLoadGroups();
        }
      }}
      className={"groups-card"}
      isReady={isReady}
      data-test-groups-card
      title={intl.formatMessage({
        defaultMessage: "User groups",
        description: "heading for the card",
      })}
      headerButton={headerButton}
      items={
        groups && groups.length
          ? groups.sort(sortToLatestByCreatedOrUpdated).map((g) => {
              const Wrapper = hasAction(groupsConf.rowActions, 'show') ? Link : "span";
              const wrapperProps: any = {
                className: "item-copy",
              };
              if (hasAction(groupsConf.rowActions, 'show')) {
                wrapperProps.to =
                  (("/groups/" + g.id) as string) + "/" + ModalKeys.show;
              }
              return (
                <>
                  <Wrapper {...wrapperProps}>
                    <TooltipWrapper
                      tipKey={"group_" + g.id}
                      tip={g.name}
                      placement={"top"}
                    >
                      <span className={"label"}>{g.name}</span>
                    </TooltipWrapper>
                    <GroupTypeBadge type={g.type} />
                  </Wrapper>
                  {(hasAction(groupsConf.rowActions, 'members') || hasAction(groupsConf.rowActions, 'invite')) && (
                    <ButtonGroup size={"sm"}>
                      {hasAction(groupsConf.rowActions, 'members') && (
                        <Link
                          className={"btn btn-secondary custom-base"}
                          to={
                            (("/groups/" + g.id) as string) +
                            "/" +
                            ModalKeys.members
                          }
                        >
                          {intl.formatMessage({
                            defaultMessage: "Members",
                            description: "label for the members button",
                          })}
                        </Link>
                      )}
                      {hasAction(groupsConf.rowActions, 'invite') && (
                        <Link
                          className={"btn btn-secondary custom-base"}
                          to={
                            (("/groups/" + g.id) as string) +
                            "/" +
                            ModalKeys.invite
                          }
                        >
                          {intl.formatMessage({
                            defaultMessage: "Invite",
                            description: "label for the invite button",
                          })}
                        </Link>
                      )}
                    </ButtonGroup>
                  )}
                </>
              );
            })
          : undefined
      }
      footer={
        <FieldList
          inline={true}
          fields={{
            total: {
              label: intl.formatMessage(SummaryCard.TotalLabel),
              value: groups ? groups.length : 0,
              itemClass: "total",
            },
          }}
        />
      }
    >
      {groups && groups.length === 0 && (
        <div data-test-no-items className={"no-data"}>
          <div className={"icon"}>{IconLibrary.customIcons.noData}</div>
          <p>
            {intl.formatMessage({
              defaultMessage: "No groups to show.",
              description: "display value for no groups",
            })}
          </p>
        </div>
      )}
    </SummaryCard>
  );
}
export default GroupsCard;
